import { CSSObject } from '@emotion/react';
import {
    FlagColor,
    getSizeValue,
    useHoneycombTheme,
    FlagElementColors,
    HoneycombTheme,
    FlagVariant,
} from '../../theming';
import { isFlagThemeableColor } from '../../utils';

const getElementColors = (color: FlagColor, theme: HoneycombTheme): FlagElementColors => {
    const { components } = theme;
    const componentTheme = components.themes.Flag;
    if (isFlagThemeableColor(color)) {
        return componentTheme.colors[color];
    }
    return componentTheme.fixedColors[color];
};

const getColorStyles = (colors: FlagElementColors, theme: HoneycombTheme): CSSObject => {
    const { background, text } = theme.fn.resolveFlatColorObject(colors);
    return {
        backgroundColor: background,
        color: text,
    };
};

export interface FlagStylesParams {
    color: FlagColor;
    variant: FlagVariant;
}

type StyleClasses = 'root';

export function useStyles({ color, variant }: FlagStylesParams): {
    styles: { [key in StyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();

    const componentTheme = theme.components.themes.Flag;
    const variantProps = componentTheme.variant[variant];

    const TypographyProps = theme.fn.getTypographyVariant(variantProps.typographyVariant);

    const { shadow, height, paddingX, radius } = componentTheme;

    const shadowValue = theme.shadows[shadow];

    const root: CSSObject = {
        display: 'inline-flex',
        alignItems: 'center',
        height: getSizeValue(height),
        boxShadow: shadowValue || 'none',
        padding: `0 ${getSizeValue(paddingX)}`,
        borderRadius: getSizeValue(radius),
        ...getColorStyles(getElementColors(color, theme), theme),
        fontFamily: TypographyProps.fontFamily,
        fontWeight: TypographyProps.fontWeight[variantProps.fontWeight],
        fontSize: TypographyProps.fontSize,
        lineHeight: 1,
        textTransform: TypographyProps.textTransform,
    };

    return {
        styles: {
            root,
        },
    };
}
